import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import ThemeMaker from "../components/thememaker";
import { httpService } from "../services/httpService";
import AddAppointment from "../pages/Calendar/BigCalendar/appointmentcalendar/AddAppointment";
import {
  STEP_KEY_MAP,
  REVERSE_KEY_MAP,
  extractCompletedStepIds,
  fetchVendorSetup,
  markSetupStepComplete,
  inferTakeOnlineChoice,
} from "../utils/vendorSetupSteps";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTimes,
  faLock,
  faExclamationTriangle,
} from "@fortawesome/free-solid-svg-icons";
import BookingDetailsDrawer from "./Orders/BookingDetailsDrawer";
import { fetchOnlineProfileCached } from "../hooks/useOnlineProfile";

// ----------------------------------------------------------------------
// Helper functions (unchanged)
// ----------------------------------------------------------------------
async function fetchOnlineProfile() {
  try {
    const response = await fetchOnlineProfileCached();
    const body = response?.data ?? response;
    
    return body?.data ?? body ?? null;
  } catch (error) {
    console.error("Failed to fetch online profile:", error);
    throw error;
  }
}

async function updateVendorSetupStep(vid, backendKey) {
  return httpService.put(`/vendorregistration/updatesetup/${vid}`, {
    steps: [{ key: backendKey, completed: true }],
  });
}

const STAFF_ROLE_OPTIONS = [
  { id: "groomer", label: "Groomer", icon: "✂️" },
  { id: "vet", label: "Veterinarian", icon: "🩺" },
  { id: "receptionist", label: "Receptionist", icon: "🗂️" },
  { id: "trainer", label: "Pet Trainer", icon: "🐾" },
  { id: "caretaker", label: "Caretaker", icon: "🏠" },
  { id: "driver", label: "Driver", icon: "🚗" },
  { id: "technician", label: "Vet Technician", icon: "💊" },
  { id: "manager", label: "Manager", icon: "👔" },
];

// ----------------------------------------------------------------------
// Main Component
// ----------------------------------------------------------------------
export default function GettingStarted() {
  const navigate = useNavigate();
  const location = useLocation();

  // ----- State -----
  const [profile, setProfile] = useState(null);
  const [vendorId, setVendorId] = useState(null);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [skippedSteps, setSkippedSteps] = useState([]);
  const [expandedStepId, setExpandedStepId] = useState(null);
  const [takeOnline, setTakeOnline] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showBookingDrawer, setShowBookingDrawer] = useState(false);
  const [selectedStaffRoles, setSelectedStaffRoles] = useState([]);
  const [showWelcomeVideoModal, setShowWelcomeVideoModal] = useState(false);
  const [welcomeVideoEnded, setWelcomeVideoEnded] = useState(false);
  const [showKycSkipModal, setShowKycSkipModal] = useState(false);
  const [bookingDetailsDrawerOpen, setBookingDetailsDrawerOpen] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState(null);

  const ytPlayerRef = useRef(null);
  const ytContainerRef = useRef(null);

  const pendingStepsRef = useRef([]);

  // ----- Helper maps (unchanged) -----
  const REVERSE_STEP_KEY_MAP = Object.fromEntries(
    Object.entries(STEP_KEY_MAP).map(([frontend, backend]) => [
      backend,
      frontend,
    ]),
  );

  const extractCompletedStepIdsFromSetup = (setupData) => {
    const rawSteps =
      setupData?.steps ??
      setupData?.data?.steps ??
      setupData?.data ??
      setupData;

    if (Array.isArray(rawSteps)) {
      return extractCompletedStepIds(rawSteps);
    }

    if (rawSteps && typeof rawSteps === "object") {
      return Object.entries(rawSteps)
        .filter(
          ([, value]) =>
            value === true || value === "true" || value === 1 || value === "1",
        )
        .map(([key]) => REVERSE_STEP_KEY_MAP[String(key).trim().toLowerCase()])
        .filter(Boolean);
    }

    return [];
  };

  const mergeStepIds = (localIds, remoteIds) => {
    const local = Array.isArray(localIds) ? localIds : [];
    const remote = Array.isArray(remoteIds) ? remoteIds : [];
    return [...new Set([...local, ...remote])];
  };

  // ----- Callbacks for DB sync (unchanged) -----
  const refreshSetupFromDB = React.useCallback(async (vid) => {
    if (!vid) return [];
    try {
      const setupData = await fetchVendorSetup(vid);
      const completedFromAPI = extractCompletedStepIdsFromSetup(setupData);
      setCompletedSteps(completedFromAPI);
      return completedFromAPI;
    } catch (err) {
      console.error("Failed to sync UI with DB:", err);
      return [];
    }
  }, []);

  const saveStepToDB = React.useCallback(
    async (stepId, vid) => {
      const backendKey = STEP_KEY_MAP[stepId];
      if (!backendKey) return false;

      const currentVid = vid || vendorId;
      if (!currentVid) {
        pendingStepsRef.current.push({ stepId, backendKey });
        return false;
      }

      try {
        await updateVendorSetupStep(currentVid, backendKey);

        const completedFromAPI = await refreshSetupFromDB(currentVid);
        if (!completedFromAPI.includes(stepId)) {
          setCompletedSteps((prev) =>
            prev.includes(stepId) ? prev : [...prev, stepId],
          );
        }

        return true;
      } catch (err) {
        console.error(`DB sync failed for step "${stepId}":`, err);
        pendingStepsRef.current.push({ stepId, backendKey });
        return false;
      }
    },
    [vendorId, refreshSetupFromDB],
  );

  const isStepCompleted = React.useCallback(
    (stepId) => {
      if (stepId === "takeOnline") return takeOnline !== null;
      return completedSteps.includes(stepId);
    },
    [completedSteps, takeOnline],
  );

  // ----- Initial data fetch -----
  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const data = await fetchOnlineProfile();
        if (cancelled) return;

        setProfile(data);

        const vid =
          data?._id ||
          data?.vendor_id ||
          data?.id ||
          data?.data?._id ||
          data?.data?.vendor_id ||
          data?.data?.id;

        if (vid) {
          setVendorId(vid);

          // Seed completed steps from onboardingProgress first (instant, no extra call)
          const onboardingSteps = data?.onboardingProgress?.steps || [];
          const completedFromOnboarding = onboardingSteps
            .filter((s) => s.completed)
            .map((s) => REVERSE_KEY_MAP[String(s.key).trim().toLowerCase()] || null)
            .filter(Boolean);

          if (completedFromOnboarding.length > 0) {
            setCompletedSteps(completedFromOnboarding);
          }

          // Then sync with the setup DB endpoint to catch any records not in onboarding
          const completedFromAPI = await refreshSetupFromDB(vid);
          if (!cancelled) {
            setTakeOnline(inferTakeOnlineChoice(data, completedFromAPI));
          }

          const pending = pendingStepsRef.current.splice(0);
          for (const { stepId } of pending) {
            try {
              await saveStepToDB(stepId, vid);
            } catch (e) {
              console.warn("Pending step retry failed:", e);
            }
          }
        }
      } catch (err) {
        console.error("Error loading setup profile from DB:", err);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [refreshSetupFromDB, saveStepToDB]);

  // Process any pending step marker placed by other pages (OnlineProfile / EmployeeDrawer)
  useEffect(() => {
    if (!vendorId) return;
    try {
      const pending = sessionStorage.getItem("pendingStepCompletion");
      if (!pending) return;
      (async () => {
        try {
          await saveStepToDB(pending, vendorId);
          sessionStorage.removeItem("pendingStepCompletion");
        } catch (e) {
          console.warn("Failed to process pendingStepCompletion:", e);
        }
      })();
    } catch (e) {
      // ignore sessionStorage failures
    }
  }, [vendorId, saveStepToDB]);

  useEffect(() => {
    checkWelcomeVideoStatus();
  }, []);
useEffect(() => {
  if (!isLoading && profile?.onboardingProgress?.completed === true) {
    navigate("/dashboard", { replace: true });
  }
}, [isLoading, profile, navigate]);
  const checkWelcomeVideoStatus = async () => {
    try {
      const response = await httpService.get("vendors/getOnlineProfile");
      const profile = response?.data || response;
      if (!profile?.welcomeVideoSeen) {
        setShowWelcomeVideoModal(true);
      }
    } catch (error) {
      console.error("Failed to fetch online profile:", error);
    }
  };

  const markWelcomeVideoSeen = async () => {
    try {
      await httpService.put("vendors/updateOnlineProfile", {
        welcomeVideoSeen: true,
      });
    } catch (error) {
      console.error("Failed to update welcomeVideoSeen flag:", error);
    }
  };
  const handleSkipWelcomeVideo = () => {
    setShowWelcomeVideoModal(false);
    markWelcomeVideoSeen();
  };

  const handleWelcomeVideoDone = () => {
    setShowWelcomeVideoModal(false);
    setWelcomeVideoEnded(false);
    markWelcomeVideoSeen();
  };

  const handleWelcomeVideoRewatch = () => {
    setWelcomeVideoEnded(false);
    if (ytPlayerRef.current?.seekTo) {
      ytPlayerRef.current.seekTo(0);
      ytPlayerRef.current.playVideo();
    }
  };

  // ----- Derived data for plan & categories -----
  // ⚠ Always resolve the ACTIVE plan (status "1" / "active") — not subscriptionPlan[0],
  //   which may be an old/inactive plan from before the vendor upgraded.
  const activePlan = useMemo(() => {
    const plans = profile?.subscriptionPlan || [];
    return (
      plans.find(
        (p) =>
          p.status === "1" ||
          p.status === 1 ||
          String(p.status).toLowerCase() === "active",
      ) || plans[0] || null
    );
  }, [profile]);

  const planCategories = useMemo(() => {
    if (activePlan?.plan_category?.length) return activePlan.plan_category;
    if (profile?.service_category?.length) return profile.service_category;
    return [];
  }, [activePlan, profile]);

  const { mainCategory, hasOptionalPOS, isPOSOnly } = useMemo(() => {
    if (!planCategories.length) {
      return { mainCategory: null, hasOptionalPOS: false, isPOSOnly: false };
    }

    const normalizeName = (cat) =>
      String(
        cat.service_category_name || cat.category_name || cat.name || "",
      ).toLowerCase();

    const hasPOS = planCategories.some((cat) => {
      const name = normalizeName(cat);
      return name.includes("pet shop") || name.includes("pos");
    });

    const nonPOSCategories = planCategories.filter((cat) => {
      const name = normalizeName(cat);
      return !name.includes("pet shop") && !name.includes("pos");
    });

    const isOnlyPOS = hasPOS && nonPOSCategories.length === 0;

    const main =
      planCategories.find((cat) => cat.is_optional === 0) || planCategories[0];

    return {
      mainCategory: main,
      hasOptionalPOS: hasPOS && !isOnlyPOS,
      isPOSOnly: isOnlyPOS,
    };
  }, [planCategories]);

  const mainType = useMemo(() => {
    if (!mainCategory) return "none";
    const name = mainCategory.service_category_name.toLowerCase();
    if (name.includes("grooming")) return "grooming";
    if (name.includes("boarding") || name.includes("kennel")) return "boarding";
    if (name.includes("vet") || name.includes("clinic")) return "vet";
    if (name.includes("pet shop") || name.includes("pos")) return "pos";
    return "other";
  }, [mainCategory]);

  // ----- Build step list driven by backend onboardingProgress -----
  // The backend (getOnlineProfile → onboardingProgress.steps) is the source of truth
  // for WHICH steps to show and whether each is mandatory. The frontend only provides
  // the UI metadata (label, description, route, actionLabel).
  const allSteps = useMemo(() => {
    const categoryName = mainCategory?.service_category_name || mainType;

    // ── UI definition library ────────────────────────────────────────────
    // Keyed by frontend step ID. These never change; only the backend decides
    // which IDs to include and whether each is mandatory.
    const STEP_LIBRARY = {
      businessHours: {
        id: "businessHours",
        label: "Set Up Business Hours",
        description: `Configure your ${mainType} business hours`,
        instructions: `Set the operating hours for your ${mainType} business.`,
        actionLabel: "Set Hours",
        route:
          mainType === "grooming"
            ? "/admin/booking-management/calendar?tab=appointment"
            : "/admin/booking-management/calendar?tab=vet",
        categoryLabel: categoryName,
      },
      client: {
        id: "client",
        label: "Create Client & Pet",
        description: "Create your first customer and their pet profile",
        instructions: "Register a client in the system along with their pet's details.",
        actionLabel: "Add Customer",
        route: "/admin/clients-pets",
        categoryLabel: null,
      },
      room: {
        id: "room",
        label: "Add Room",
        description: "Set up rooms for boarding",
        instructions: "Create rooms where pets will stay during boarding.",
        actionLabel: "Add Room",
        route: "/admin/room_management",
        categoryLabel: categoryName,
      },
      service: {
        id: "service",
        label: `Create ${
          mainType === "grooming"
            ? "Grooming"
            : mainType === "boarding"
              ? "Boarding"
              : "Veterinary"
        } Service`,
        description: `Define your ${mainType} service catalog & pricing`,
        instructions: "Set up your services by adding pricing, duration, and service charges.",
        actionLabel: "Create Service",
        route:
          mainType === "grooming"
            ? "/admin/service-management/grooming"
            : mainType === "boarding"
              ? "/admin/service-management/boarding"
              : "/admin/service-management/vet_clinic",
        categoryLabel: categoryName,
      },
      booking: {
        id: "booking",
        label: "Create Booking",
        description: "Create your first booking",
        instructions: "Schedule your first booking for a client.",
        actionLabel: "Create Booking",
        route: "/admin/booking/create",
        categoryLabel: null,
      },
      product: {
        id: "product",
        label: "Create Product",
        description: "Add products to your inventory",
        instructions: "Add products to your shop inventory.",
        actionLabel: "Add Product",
        route: "/admin/inventory-management/products",
        categoryLabel: null,
      },
      supplier: {
        id: "supplier",
        label: "Create Supplier",
        description: "Add your product suppliers",
        instructions: "Add the suppliers from whom you purchase your products.",
        actionLabel: "Create Supplier",
        route: "/admin/inventory-management/suppliers",
        categoryLabel: null,
      },
      purchaseBill: {
        id: "purchaseBill",
        label: "Create Purchase Bill",
        description: "Record your first purchase from a supplier",
        instructions: "Create a purchase bill to record incoming stock.",
        actionLabel: "Create Purchase Bill",
        route: "/admin/inventory-management/purchase_bills/new",
        categoryLabel: null,
      },
      posSale: {
        id: "posSale",
        label: "Create a POS Sale",
        description: "Create your first point\u2011of\u2011sale transaction",
        instructions: "Process a sale using the POS system.",
        actionLabel: "Create POS Sale",
        route: "/pos",
        categoryLabel: null,
      },
      businessKyc: {
        id: "businessKyc",
        label: "Complete Business KYC",
        description: "Verify your business identity",
        instructions: "Provide the required KYC documents to activate your account.",
        actionLabel: "Go to KYC",
        route: "/admin/settings/general_settings?tab=businessKYC",
        categoryLabel: categoryName,
      },
      onlineProfile: {
        id: "onlineProfile",
        label: "Set Up Online Profile",
        description: "Complete your business online profile",
        instructions: "Set up your online profile to showcase your business.",
        actionLabel: "Configure Online Profile",
        route: "/admin/settings/online_profile",
        categoryLabel: null,
      },
      staff: {
        id: "staff",
        label: "Create Staff (Optional)",
        description: "Invite team members and assign roles",
        instructions: "Add additional staff to manage bookings and daily service tasks.",
        actionLabel: "Add Staff",
        route: "/admin/staff-management/employees",
        categoryLabel: null,
      },
    };

    const completionStep = {
      id: "completion",
      label: "\uD83C\uDF89 Congratulations!",
      description: "Your account setup is complete.",
      instructions: "You have successfully configured your account.",
      isOptional: false,
      categoryLabel: null,
    };

    // ── Source: backend onboardingProgress.steps ─────────────────────────
    const backendSteps = profile?.onboardingProgress?.steps;

    let steps = [];

    if (backendSteps?.length) {
      // Track whether backend includes an online_profile step so we can
      // inject the takeOnline question step immediately before it.
      let hasOnlineProfileInBackend = false;

      for (const bs of backendSteps) {
        const normalizedKey = String(bs.key || "")
          .trim()
          .toLowerCase();
        const frontendId = REVERSE_KEY_MAP[normalizedKey];
        if (!frontendId) continue;

        // Capture that the backend knows about online_profile (regardless of state)
        if (frontendId === "onlineProfile") {
  hasOnlineProfileInBackend = true;
  continue;
}

const def = STEP_LIBRARY[frontendId];
if (!def) continue;

steps.push({
  ...def,
  isOptional: bs.mandatory === false,
  _backendCompleted: bs.completed === true, // (optional flag, not required)
});
      }

      // Inject takeOnline + (conditionally) onlineProfile at the end of required steps
if (hasOnlineProfileInBackend) {
  const onlineProfileDone = completedSteps.includes("onlineProfile");


  if (takeOnline === null && !onlineProfileDone) {
    steps.push({
      id: "takeOnline",
      label: "Take Your Business Online",
      description: "Do you want to enable online presence and booking?",
      instructions: 'Choosing "Yes" allows you to set up your online profile.',
      isOptional: false,
      categoryLabel: null,
    });
  }

  if (takeOnline === true && !onlineProfileDone) {
    steps.push({ ...STEP_LIBRARY.onlineProfile, isOptional: false });
  }
}
    } else {
      // ── Fallback: keep original plan-category logic if backend has no steps ──
      const buildStep = (
        id, label, description, instructions, actionLabel, route,
        isOptional = false, categoryLabel = null,
      ) => ({ id, label, description, instructions, actionLabel, route, isOptional, categoryLabel });

      if (isPOSOnly) {
        steps = [
          { ...STEP_LIBRARY.product, isOptional: false },
          { ...STEP_LIBRARY.supplier, isOptional: false },
          { ...STEP_LIBRARY.purchaseBill, isOptional: false },
          { ...STEP_LIBRARY.posSale, isOptional: false },
          { ...STEP_LIBRARY.staff, isOptional: true },
        ];
      } else {
        if (mainType === "grooming" || mainType === "vet") steps.push({ ...STEP_LIBRARY.businessHours, isOptional: false });
        steps.push({ ...STEP_LIBRARY.client, isOptional: false });
        if (mainType === "boarding") steps.push({ ...STEP_LIBRARY.room, isOptional: false });
        if (mainType !== "none" && mainType !== "other" && mainType !== "pos") steps.push({ ...STEP_LIBRARY.service, isOptional: false });
        if (mainType !== "none" && mainType !== "other" && mainType !== "pos") steps.push({ ...STEP_LIBRARY.booking, isOptional: false });
        if (hasOptionalPOS) { steps.push({ ...STEP_LIBRARY.product, isOptional: false }); steps.push({ ...STEP_LIBRARY.posSale, isOptional: false }); }
        steps.push({ ...STEP_LIBRARY.businessKyc, isOptional: false });
        if (mainType !== "pos" && !isPOSOnly) {
          steps.push({ id: "takeOnline", label: "Take Your Business Online", description: "Do you want to enable online presence and booking?", instructions: 'Choosing "Yes" allows you to set up your online profile.', isOptional: false, categoryLabel: null });
          if (takeOnline === true) steps.push({ ...STEP_LIBRARY.onlineProfile, isOptional: false });
        }
        steps.push({ ...STEP_LIBRARY.staff, isOptional: true });
      }
    }

    // ── Completion step (appended when all required steps are done) ───────
    const requiredStepIds = steps
      .filter((s) => !s.isOptional && s.id !== "takeOnline" && s.id !== "onlineProfile")
      .map((s) => s.id);

    const allRequiredDone =
      requiredStepIds.length > 0 &&
      requiredStepIds.every((id) => completedSteps.includes(id));

    if (allRequiredDone) steps.push(completionStep);

    return steps;
  }, [
    profile,
    mainType,
    isPOSOnly,
    hasOptionalPOS,
    takeOnline,
    completedSteps,
    mainCategory,
    skippedSteps,
  ]);

  // ----- Required steps (for progress) (unchanged) -----
  const requiredSteps = useMemo(() => {
    return allSteps.filter(
      (s) =>
        !s.isOptional &&
        s.id !== "takeOnline" &&
        s.id !== "onlineProfile" &&
        s.id !== "completion",
    );
  }, [allSteps]);

  const totalRequired = requiredSteps.length;
  const completedRequired = requiredSteps.filter((s) =>
    isStepCompleted(s.id),
  ).length;
  const displayProgress =
    totalRequired > 0
      ? Math.round((completedRequired / totalRequired) * 100)
      : 0;

 const isSetupComplete =
   profile?.onboardingProgress?.completed === true ||
   (totalRequired > 0 && completedRequired >= totalRequired);

  useEffect(() => {
    try {
      sessionStorage.setItem(
        "vendorSetupComplete",
        isSetupComplete ? "true" : "false",
      );
    } catch (e) {
      // ignore storage failure
    }
  }, [isSetupComplete]);

  // Effect for when user selects "No" for online (now handled via skip)
  useEffect(() => {
    if (
      takeOnline === false &&
      vendorId &&
      !completedSteps.includes("onlineProfile")
    ) {
      // If we set takeOnline false, we also mark onlineProfile as completed (skipped)
      void saveStepToDB("onlineProfile", vendorId);
    }
  }, [takeOnline, vendorId, completedSteps, saveStepToDB]);

  // Effect to sync when returning from a completed step
  useEffect(() => {
    const stepId = location.state?.stepCompleted;
    if (!stepId) return;

    navigate(location.pathname, { replace: true, state: {} });

    setCompletedSteps((prev) =>
      prev.includes(stepId) ? prev : [...prev, stepId],
    );

    if (!vendorId) {
      const backendKey = STEP_KEY_MAP[stepId];
      if (backendKey) pendingStepsRef.current.push({ stepId, backendKey });
      return;
    }

    void saveStepToDB(stepId, vendorId);
  }, [location.state, navigate, vendorId, saveStepToDB]);

  // Compute locked status for each step
  const lockedSteps = useMemo(() => {
    const locked = {};
    for (const step of allSteps) {
      // Completed or skipped steps are not locked
      if (completedSteps.includes(step.id) || skippedSteps.includes(step.id)) {
        locked[step.id] = false;
        continue;
      }

      const index = allSteps.findIndex((s) => s.id === step.id);
      if (index === -1) {
        locked[step.id] = false;
        continue;
      }

      // Previous steps in order
      const previousSteps = allSteps.slice(0, index);

      // Core required steps: non-optional, not special
      const coreRequiredPrevious = previousSteps.filter(
        (s) =>
          !s.isOptional &&
          s.id !== "takeOnline" &&
          s.id !== "onlineProfile" &&
          s.id !== "completion",
      );

      // Lock if any core required previous step is neither completed nor skipped
      const anyIncomplete = coreRequiredPrevious.some(
        (s) =>
          !completedSteps.includes(s.id) && !skippedSteps.includes(s.id),
      );

      locked[step.id] = anyIncomplete;
    }
    return locked;
  }, [allSteps, completedSteps, skippedSteps]);

  // ----- UI handlers -----
  const handleAction = (step) => {
    // Prevent action if locked
    if (lockedSteps[step.id]) return;

    // Remove from skipped if it was skipped
    setSkippedSteps((prev) => prev.filter((id) => id !== step.id));

    if (step.id === "booking") {
      setShowBookingDrawer(true);
      return;
    }

    if (step.id === "staff") {
      navigate(step.route, {
        state: {
          fromGettingStarted: true,
          returnTo: location.pathname,
          stepId: step.id,
          autoOpen: "staff",
          selectedRoles: selectedStaffRoles,
        },
      });
      return;
    }

    const navState = {
      fromGettingStarted: true,
      returnTo: location.pathname,
      autoOpen: step.id,
      stepId: step.id,
      openBusinessHoursDrawer: step.id === "businessHours",
    };

    if (step.id === "client") {
      navigate(step.route, {
        state: {
          ...navState,
          openAddDrawer: true,
          hideSaveButton: true,
        },
      });
      return;
    }

    navigate(step.route, { state: navState });
  };

  // Modified skip handler – shows modal for businessKyc
  const handleSkipStep = async (step) => {
    if (lockedSteps[step.id]) return;

    // For businessKyc, show confirmation modal first
    if (step.id === "businessKyc") {
      setShowKycSkipModal(true);
      return; // will be handled after confirmation
    }

    // For takeOnline, skip means "No" – set takeOnline false and mark onlineProfile as skipped
    if (step.id === "takeOnline") {
      setTakeOnline(false);
      // Mark onlineProfile as completed (since we skip it)
      if (vendorId) {
        await saveStepToDB("onlineProfile", vendorId);
      } else {
        sessionStorage.setItem("pendingStepCompletion", "onlineProfile");
      }
      return;
    }

    // For onlineProfile, we mark them as completed (skipped)
    if (step.id === "onlineProfile") {
      if (!completedSteps.includes(step.id)) {
        setCompletedSteps((prev) => [...prev, step.id]);
        if (vendorId) {
          await saveStepToDB(step.id, vendorId);
        } else {
          sessionStorage.setItem("pendingStepCompletion", step.id);
        }
      }
      return;
    }

    // General skip for optional steps (only if marked optional)
    if (step.isOptional) {
      if (!skippedSteps.includes(step.id)) {
        setSkippedSteps((prev) => [...prev, step.id]);
      }

      if (step.id === "staff" && !completedSteps.includes(step.id)) {
        setCompletedSteps((prev) =>
          prev.includes(step.id) ? prev : [...prev, step.id],
        );
        if (vendorId) {
          await saveStepToDB(step.id, vendorId);
        } else {
          sessionStorage.setItem("pendingStepCompletion", step.id);
        }
      }
    }
  };

  // Confirm skip for KYC
  const confirmKycSkip = async () => {
    setShowKycSkipModal(false);
    // Mark KYC as skipped
    if (!skippedSteps.includes("businessKyc")) {
      setSkippedSteps((prev) => [...prev, "businessKyc"]);
    }
    // Save to DB as completed (so it won't appear again)
    if (vendorId) {
      await saveStepToDB("businessKyc", vendorId);
    } else {
      sessionStorage.setItem("pendingStepCompletion", "businessKyc");
    }
  };

  // ----- Loading state -----
  if (isLoading) {
    return (
      <ThemeMaker>
        <div className="flex justify-center items-center h-64">
          <div className="text-gray-500">Loading setup steps...</div>
        </div>
      </ThemeMaker>
    );
  }

  const planName = activePlan?.plan_name || "your selected plan";
  const categoryDisplayName = mainCategory?.service_category_name || mainType;

  // --------------------------------------------------------------------
  // Render
  // --------------------------------------------------------------------
  return (
    <ThemeMaker>
      <div className="w-full py-6 px-4">
        <div className="bg-white rounded-xl shadow-lg overflow-hidden relative max-h-[calc(100vh-12rem)] overflow-y-auto">
          {/* Header with progress bar */}
          <div className="sticky top-0 z-10 px-6 py-6 bg-gradient-to-r from-blue-600 to-indigo-700 text-white shadow-md">
            <h1 className="text-2xl font-bold">Welcome to FLPS</h1>
            <p className="mt-1 text-blue-100 text-sm">
              You are subscribed to the <strong>{planName}</strong> plan.
              <br />
              {categoryDisplayName && (
                <span className="inline-block mt-1 px-2 py-0.5 bg-white/20 rounded-full text-xs font-medium">
                  Category: {categoryDisplayName}
                </span>
              )}
              <br />
              Complete these steps to set up your account and start serving
              customers.
            </p>
            <div className="mt-4">
              <div className="flex justify-between text-sm font-medium text-blue-100 mb-1">
                <span>Setup Progress</span>
                <span>{displayProgress}%</span>
              </div>
              <div className="w-full bg-blue-300/30 rounded-full h-2.5">
                <div
                  className="bg-white h-2.5 rounded-full transition-all duration-700 ease-out"
                  style={{ width: `${displayProgress}%` }}
                />
              </div>
            </div>
          </div>

          {/* Steps list - simplified rows */}
          <div className="divide-y divide-gray-200">
            {allSteps.map((step, index) => {
              const isCompleted = isStepCompleted(step.id);
              const isSkipped = skippedSteps.includes(step.id);
              const locked = lockedSteps[step.id] || false;

              // Completion step special rendering
              if (step.id === "completion") {
                return (
                  <div
                    key={step.id}
                    className="px-5 py-6 bg-green-50 border-t border-green-200 text-center"
                  >
                    <h2 className="text-xl font-bold text-green-700">
                      {step.label}
                    </h2>
                    <p className="text-gray-700 mt-2">{step.instructions}</p>
                    <button
                      onClick={async () => {
                        try {
                          // 1. Ensure onlineProfile is marked as completed if it was skipped
                          if (!completedSteps.includes("onlineProfile")) {
                            await saveStepToDB("onlineProfile", vendorId);
                          }

                          // 2. Force recalculation of the overall progress
                          await httpService.put(
                            `/vendorregistration/updatesetup/${vendorId}`,
                            {
                              steps: [], // triggers backend recompute
                            },
                          );
                        } catch (err) {
                          console.error("Failed to finalize setup:", err);
                        }
                        navigate("/dashboard");
                      }}
                      className="mt-4 px-6 py-2 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg shadow-sm transition"
                    >
                      Go to Dashboard
                    </button>
                  </div>
                );
              }

              // Determine if the step is "takeOnline" – we need special buttons
              const isTakeOnline = step.id === "takeOnline";
              // Steps that support skip (we show skip button for these)
              const supportsSkip =
                isTakeOnline ||
                step.id === "businessKyc" ||
                step.id === "onlineProfile" ||
                step.isOptional;

              const isLocked = locked;

              // Check if KYC is skipped to disable "Yes" for takeOnline
              const isKycSkipped = skippedSteps.includes("businessKyc");

              return (
                <div
                  key={step.id}
                  className={`px-5 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors ${
                    isCompleted || isSkipped ? "opacity-70" : ""
                  } ${isLocked ? "opacity-50 cursor-not-allowed" : ""}`}
                >
                  <div className="flex items-center gap-4 flex-1 min-w-0">
                    {/* Step number / status icon */}
                    <div
                      className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                        isCompleted
                          ? "bg-green-100 text-green-700"
                          : isSkipped
                            ? "bg-amber-100 text-amber-700"
                            : isLocked
                              ? "bg-gray-200 text-gray-400"
                              : "bg-gray-100 text-gray-500"
                      }`}
                    >
                      {isCompleted ? (
                        <svg
                          className="w-5 h-5"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      ) : isSkipped ? (
                        <span className="text-xs">Skip</span>
                      ) : isLocked ? (
                        <FontAwesomeIcon icon={faLock} className="text-sm" />
                      ) : (
                        index + 1
                      )}
                    </div>

                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3
                          className={`text-base font-semibold ${
                            isCompleted
                              ? "text-gray-400 line-through"
                              : isLocked
                                ? "text-gray-400"
                                : "text-gray-800"
                          }`}
                        >
                          {step.label}
                        </h3>
                        {step.categoryLabel && (
                          <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">
                            For {step.categoryLabel}
                          </span>
                        )}
                        {step.isOptional && (
                          <span className="text-[10px] font-medium uppercase px-2 py-0.5 bg-gray-200 text-gray-600 rounded-full">
                            Optional
                          </span>
                        )}
                        {!step.isOptional &&
                          step.id !== "takeOnline" &&
                          step.id !== "onlineProfile" && (
                            <span className="text-[10px] font-medium uppercase px-2 py-0.5 bg-red-100 text-red-700 rounded-full">
                              Required
                            </span>
                          )}
                      </div>
                      <p className="text-sm text-gray-500 truncate">
                        {step.description}
                      </p>
                    </div>
                  </div>

                  {/* Right side: status badge + action buttons */}
                  <div className="flex items-center gap-3 flex-shrink-0 ml-4">
                    {isCompleted && (
                      <span className="text-xs font-medium text-green-600 bg-green-50 px-2 py-1 rounded-full border border-green-200">
                        Done
                      </span>
                    )}
                    {isSkipped && !isCompleted && (
                      <span className="text-xs font-medium text-amber-600 bg-amber-50 px-2 py-1 rounded-full border border-amber-200">
                        Skipped
                      </span>
                    )}

                    {!isCompleted && !isSkipped && (
                      <>
                        {isTakeOnline ? (
                          // Special inline buttons for Take Online: Yes and Skip
                         <div className="flex items-center gap-2">

                          
    <button
      onClick={() => {
        setTakeOnline(true);
        // Navigate to online profile – always allowed
        navigate("/admin/settings/online_profile", {
          state: {
            fromGettingStarted: true,
            returnTo: location.pathname,
            stepId: "onlineProfile",
            autoOpen: "onlineProfile",
          },
        });
      }}
      disabled={isLocked}   // Only locked if previous required steps are incomplete
      className={`px-3 py-1.5 text-sm font-semibold rounded-lg shadow-sm transition ${
        isLocked
          ? "bg-gray-300 text-gray-500 cursor-not-allowed"
          : "bg-green-600 hover:bg-green-700 text-white"
      }`}
    >
      Yes
    </button>
    <button
      onClick={() => handleSkipStep(step)}
      disabled={isLocked}
      className={`px-3 py-1.5 text-sm font-semibold rounded-lg shadow-sm transition ${
        isLocked
          ? "bg-gray-300 text-gray-500 cursor-not-allowed"
          : "bg-gray-200 hover:bg-gray-300 text-gray-800"
      }`}
    >
      Skip
    </button>
  </div>
                        ) : (
                          // Regular action button
                          <button
                            onClick={() => handleAction(step)}
                            disabled={isLocked}
                            className={`px-4 py-1.5 text-sm font-semibold rounded-lg shadow-sm transition flex items-center gap-1 ${
                              isLocked
                                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                                : "bg-blue-600 hover:bg-blue-700 text-white"
                            }`}
                          >
                            {step.actionLabel || "Go"}
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M14 5l7 7m0 0l-7 7m7-7H3"
                              />
                            </svg>
                          </button>
                        )}

                        {/* Skip button for businessKyc, onlineProfile, and optional steps */}
                        {supportsSkip && !isTakeOnline && (
                          <button
                            onClick={() => handleSkipStep(step)}
                            disabled={isLocked}
                            className={`px-3 py-1.5 text-xs font-medium rounded-lg transition ${
                              isLocked
                                ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                                : "bg-gray-100 hover:bg-gray-200 text-gray-600"
                            }`}
                          >
                            Skip
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Footer
          <div className="px-5 py-3 bg-gray-50 border-t border-gray-200 text-sm text-gray-600 flex justify-between items-center sticky bottom-0 bg-gray-50">
            <div>
              <span className="font-semibold text-gray-800">
                {completedRequired}
              </span>{" "}
              of{" "}
              <span className="font-semibold text-gray-800">
                {totalRequired}
              </span>{" "}
              required steps completed
              {skippedSteps.length > 0 && (
                <span className="text-xs text-amber-600 ml-2">
                  ({skippedSteps.length} skipped)
                </span>
              )}
            </div>
            <button
              type="button"
              onClick={() => navigate("/dashboard")}
              className="px-4 py-1.5 text-sm font-semibold rounded-lg border border-gray-300 hover:bg-gray-100 text-gray-700 transition"
            >
              Go to Dashboard
            </button>
          </div> */}
        </div>

        {/* KYC Skip Warning Modal */}
        {showKycSkipModal && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[70] flex items-center justify-center p-4">
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md relative">
              <button
                className="absolute top-4 right-4 w-8 h-8 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center text-gray-500 hover:text-gray-700 z-10"
                onClick={() => setShowKycSkipModal(false)}
              >
                <FontAwesomeIcon icon={faTimes} className="text-sm" />
              </button>
              <div className="p-6 text-center">
                <FontAwesomeIcon
                  icon={faExclamationTriangle}
                  className="text-amber-500 text-5xl mb-4"
                />
                <h3 className="text-xl font-bold text-gray-800 mb-2">
                  Skip KYC?
                </h3>
                <p className="text-gray-600 mb-6">
                  If you skip Business KYC, you will <strong>not</strong> be
                  able to take your profile online. This means you cannot
                  enable online booking or public visibility.
                  <br />
                  <br />
                  Are you sure you want to skip?
                </p>
                <div className="flex gap-3 justify-center">
                  <button
                    onClick={() => setShowKycSkipModal(false)}
                    className="px-6 py-2 rounded-xl border-2 border-gray-300 text-gray-700 font-semibold hover:bg-gray-100 transition"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmKycSkip}
                    className="px-6 py-2 rounded-xl bg-red-600 hover:bg-red-700 text-white font-semibold transition"
                  >
                    Skip Anyway
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Welcome Video Modal - unchanged */}
        {/* {showWelcomeVideoModal && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl relative">
              <button
                className="absolute top-4 right-4 w-8 h-8 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center text-gray-500 hover:text-gray-700 z-10"
                onClick={handleSkipWelcomeVideo}
              >
                <FontAwesomeIcon icon={faTimes} className="text-sm" />
              </button>

              <div className="p-6">
                <h2 className="text-2xl font-bold text-gray-800 mb-4 text-center">
                  New here? This quick tutorial will guide you through the
                  system features.
                </h2>

                <div className="aspect-video w-full rounded-xl overflow-hidden bg-black">
                  <iframe
                    className="w-full h-full"
                    src="https://www.youtube.com/embed/YOUR_VIDEO_ID?autoplay=1&rel=0&modestbranding=1"
                    title="Welcome video"
                    frameBorder="0"
                    allow="autoplay; encrypted-media"
                    allowFullScreen
                  />
                </div>

                <div className="mt-6 flex justify-center gap-4">
                  {!welcomeVideoEnded ? (
                    <button
                      onClick={handleSkipWelcomeVideo}
                      className="px-6 py-2 rounded-xl border-2 border-gray-300 text-gray-700 font-semibold hover:bg-gray-100 transition-colors duration-200"
                    >
                      Skip
                    </button>
                  ) : (
                    <>
                      <button
                        onClick={handleWelcomeVideoDone}
                        className="px-6 py-2 rounded-xl bg-gradient-to-r from-orange-500 to-orange-600 text-white font-semibold hover:from-orange-600 hover:to-orange-700 transition-colors duration-200"
                      >
                        Done
                      </button>
                      <button
                        onClick={handleWelcomeVideoRewatch}
                        className="px-6 py-2 rounded-xl border-2 border-orange-500 text-orange-500 font-semibold hover:bg-orange-50 transition-colors duration-200"
                      >
                        Rewatch
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        )} */}
      </div>

      {/* Booking drawer - unchanged */}
      <AddAppointment
        isOpen={showBookingDrawer}
        onClose={() => setShowBookingDrawer(false)}
        clearSlotSelection={() => {}}
        selectedSlotInfo={null}
        editMode={false}
        editingAppointment={null}
        appointmentStatusRef={{ current: null }}
        onSave={async () => {
          setShowBookingDrawer(false);
          await saveStepToDB("booking", vendorId);
        }}
        openBookingDetailsDrawer={(orderId) => {
          setSelectedOrderId(orderId);
          setShowBookingDrawer(false);
          setBookingDetailsDrawerOpen(true);
        }}
      />
      <BookingDetailsDrawer
        isOpen={bookingDetailsDrawerOpen}
        onClose={() => {setBookingDetailsDrawerOpen(false); setSelectedOrderId(null);}}
        orderId={selectedOrderId}
        onSave={async () => {
          setBookingDetailsDrawerOpen(false);
          setSelectedOrderId(null);
        }}
      />
    </ThemeMaker>
  );
}